import React, { useState, useEffect } from 'react';
import { X, Home, MapPin, DollarSign, Maximize2, Calendar, MessageSquare, Clipboard } from 'lucide-react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';
import type { Client, FormData, PropertyClientMatch } from '../types';

interface ManuallyMatchedPropertiesModalProps {
  client: Client;
  onClose: () => void;
}

const ManuallyMatchedPropertiesModal: React.FC<ManuallyMatchedPropertiesModalProps> = ({ client, onClose }) => {
  const [properties, setProperties] = useState<FormData[]>([]);
  const [matches, setMatches] = useState<PropertyClientMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNotes, setShowNotes] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    loadManuallyMatchedProperties();
  }, [client]);

  const loadManuallyMatchedProperties = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);

      const matchesRef = collection(db, 'property_client_matches');
      const matchesQuery = query(
        matchesRef,
        where('clientId', '==', client.id),
        where('userId', '==', auth.currentUser.uid)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const matchesData = matchesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PropertyClientMatch[];

      setMatches(matchesData);

      if (matchesData.length === 0) {
        setProperties([]);
        return;
      }

      const formIds = matchesData.map(m => m.formId);
      const formsRef = collection(db, 'forms');
      const q = query(formsRef, where('userId', '==', auth.currentUser.uid));
      const snapshot = await getDocs(q);

      const allProperties = snapshot.docs.map(doc => ({
        formId: doc.id,
        ...doc.data()
      })) as FormData[];

      const matchedProperties = allProperties.filter(prop => formIds.includes(prop.formId));
      setProperties(matchedProperties);

    } catch (error) {
      console.error('Error loading manually matched properties:', error);
      toast.error('שגיאה בטעינת הנכסים המותאמים');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async (formId: string) => {
    if (!noteText.trim()) return;

    try {
      const match = matches.find(m => m.formId === formId);
      if (!match) return;

      await updateDoc(doc(db, 'property_client_matches', match.id), {
        notes: noteText.trim(),
        updatedAt: new Date().toISOString()
      });

      setMatches(prev => prev.map(m =>
        m.id === match.id ? { ...m, notes: noteText.trim() } : m
      ));

      setNoteText('');
      setShowNotes(null);
      toast.success('ההערה נשמרה בהצלחה');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('שגיאה בשמירת ההערה');
    }
  };

  const getMatchForProperty = (formId: string) => {
    return matches.find(m => m.formId === formId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-gray-800 rounded-lg w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-purple-600 to-purple-700">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Clipboard size={28} className="text-white" />
              <h2 className="text-2xl font-bold text-white">
                נכסים שהתאמתי ללקוח {client.name}
              </h2>
            </div>
            <p className="text-purple-100">
              נמצאו {properties.length} נכסים שהתאמת ידנית
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                <p className="text-gray-400">טוען נכסים...</p>
              </div>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-12">
              <Clipboard size={64} className="mx-auto text-gray-600 mb-4" />
              <p className="text-xl text-gray-400 mb-2">לא התאמת נכסים ללקוח זה</p>
              <p className="text-gray-500">
                התאם נכסים ללקוח זה דרך מסך Property Actions
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {properties.map(property => {
                const match = getMatchForProperty(property.formId);
                const isNotesOpen = showNotes === property.formId;

                return (
                  <div
                    key={property.formId}
                    className="bg-gray-750 border border-gray-700 rounded-lg overflow-hidden hover:border-purple-500 transition-all"
                  >
                    <div className="p-6">
                      <div className="bg-purple-600 rounded-t-lg -mx-6 -mt-6 mb-4 px-6 py-3">
                        <div className="flex items-center gap-2 text-white font-bold text-lg">
                          <Clipboard size={20} />
                          <span>התאמה ידנית</span>
                          {match?.createdAt && (
                            <span className="text-purple-200 text-sm font-normal mr-auto">
                              הותאם ב-{new Date(match.createdAt).toLocaleDateString('he-IL')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">
                              {property.property.propertyTypeLabel}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <MapPin size={16} />
                            <span>
                              {property.address.street} {property.address.houseNumber}, {property.address.city}
                            </span>
                          </div>
                        </div>

                        {property.imageUrls?.propertyImages?.[0] && (
                          <img
                            src={property.imageUrls.propertyImages[0]}
                            alt="נכס"
                            className="w-32 h-32 object-cover rounded-lg"
                          />
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <DollarSign size={16} />
                            <span>מחיר</span>
                          </div>
                          <p className="text-white font-bold">
                            ₪{property.property.price.toLocaleString()}
                          </p>
                        </div>

                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <Home size={16} />
                            <span>חדרים</span>
                          </div>
                          <p className="text-white font-bold">{property.property.rooms}</p>
                        </div>

                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <Maximize2 size={16} />
                            <span>גודל</span>
                          </div>
                          <p className="text-white font-bold">{property.property.size} מ"ר</p>
                        </div>

                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <Calendar size={16} />
                            <span>בשוק</span>
                          </div>
                          <p className="text-white font-bold">{property.property.daysOnMarket} ימים</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                          קומה {property.property.floor}
                        </span>
                        <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                          {property.property.bathrooms} חדרי רחצה
                        </span>
                        <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                          {property.property.parkingSpots} חניות
                        </span>
                        {property.property.hasBalcony && (
                          <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                            מרפסת {property.property.balconySize}מ"ר
                          </span>
                        )}
                        {property.property.hasElevator && (
                          <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                            מעלית
                          </span>
                        )}
                        {property.property.hasSaferoom && (
                          <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                            חדר מוגן
                          </span>
                        )}
                      </div>

                      {match?.notes && !isNotesOpen && (
                        <div className="bg-gray-700 rounded-lg p-3 mb-3">
                          <div className="flex items-start gap-2">
                            <MessageSquare size={16} className="text-purple-400 mt-1" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-400 mb-1">הערה:</p>
                              <p className="text-sm text-gray-300">{match.notes}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="border-t border-gray-700 pt-4">
                        {isNotesOpen ? (
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-400">
                              הערה על ההתאמה
                            </label>
                            <textarea
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="הוסף הערה על ההתאמה הזו..."
                              className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500 h-24"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveNote(property.formId)}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                              >
                                שמור הערה
                              </button>
                              <button
                                onClick={() => {
                                  setShowNotes(null);
                                  setNoteText('');
                                }}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                              >
                                ביטול
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setShowNotes(property.formId);
                              setNoteText(match?.notes || '');
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                          >
                            <MessageSquare size={16} />
                            {match?.notes ? 'ערוך הערה' : 'הוסף הערה'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-700 bg-gray-750 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManuallyMatchedPropertiesModal;
